/**
 * Injects a CSS rule into every open shadow root inside a host subtree, and
 * keeps doing so as more shadow roots mount (ADO-1412).
 *
 * The embedded Firefly editor is a prebuilt bundle that renders its UI into
 * (open) shadow DOM and exposes no config flag or CSS `::part` for the pieces
 * Bridgeway needs to suppress. App-level CSS can't pierce a shadow boundary, so the
 * only in-repo lever is to push a `display:none` rule INTO each shadow root. We
 * adopt a constructable stylesheet (durable across Lit re-renders) — falling
 * back to an injected `<style>` where constructable sheets are unavailable
 * (jsdom, older engines) — and observe every root, since a `MutationObserver`
 * with `subtree:true` does NOT cross shadow boundaries: each root needs its own.
 */

export interface ShadowStyleController {
	/** Re-scan the host subtree now (e.g. once the editor signals ready). */
	refresh: () => void;
	/** Stop observing and release all observers. */
	disconnect: () => void;
}

export interface ShadowStyleSpec {
	/** CSS injected into each shadow root (e.g. `selector{display:none!important}`). */
	rule: string;
	/** Attribute marking the fallback `<style>`, so it's injected at most once per root. */
	marker: string;
}

export function injectShadowCss(
	container: HTMLElement,
	{ rule, marker }: ShadowStyleSpec,
): ShadowStyleController {
	// Lazily constructed so environments without constructable stylesheets
	// (older engines, some test runtimes) fall back to an injected <style>.
	let sheet: CSSStyleSheet | null | undefined;
	const getSheet = (): CSSStyleSheet | null => {
		if (sheet !== undefined) return sheet;
		try {
			const s = new CSSStyleSheet();
			s.replaceSync(rule);
			sheet = s;
		} catch {
			sheet = null;
		}
		return sheet;
	};

	const adopt = (root: ShadowRoot) => {
		const s = getSheet();
		if (s) {
			try {
				if (!root.adoptedStyleSheets.includes(s)) {
					root.adoptedStyleSheets = [...root.adoptedStyleSheets, s];
				}
				return;
			} catch {
				// Reassigning adoptedStyleSheets is unsupported — use a <style>.
			}
		}
		if (!root.querySelector(`style[${marker}]`)) {
			const style = document.createElement('style');
			style.setAttribute(marker, '');
			style.textContent = rule;
			root.appendChild(style);
		}
	};

	const observers: MutationObserver[] = [];
	const observed = new WeakSet<Node>();
	const observe = (target: Node) => {
		if (observed.has(target)) return;
		const obs = new MutationObserver(schedule);
		obs.observe(target, { childList: true, subtree: true });
		observers.push(obs);
		observed.add(target);
	};

	const visit = (el: Element) => {
		const root = el.shadowRoot;
		if (!root) return;
		adopt(root);
		// A shadow root's adopted styles only reach its own tree, so observe and
		// descend into each root to cover the target wherever it is rendered.
		observe(root);
		root.querySelectorAll('*').forEach(visit);
	};

	const walk = () => {
		observe(container);
		container.querySelectorAll('*').forEach(visit);
	};

	// Coalesce mutation bursts (canvas paints, tool switches) into one walk/tick.
	let scheduled = false;
	const schedule = () => {
		if (scheduled) return;
		scheduled = true;
		queueMicrotask(() => {
			scheduled = false;
			walk();
		});
	};

	walk();

	return {
		refresh: walk,
		disconnect: () => {
			observers.forEach((o) => o.disconnect());
			observers.length = 0;
		},
	};
}
